import { Card } from 'antd';
import * as eva from 'eva-icons';
import { filter, includes, map } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import {
  BREAKPOINTS,
  TIME_12_HOUR_FORMAT,
  TIME_24_HOUR_FORMAT,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import EllipsisText from '../../components/EllipsisText';
import RegisterCommentModal from './RegisterCommentModal';
import RegisterCommentPopUp from './RegisterCommentPopUp';

const RegisterLogDetailsSection = ({
  data,
  refetch,
  setCommentData,
  commentData,
  loading,
  onScroll,
}) => {
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const registerDetails = [...(data?.register?.registerDetails || [])]?.sort(
    (a, b) => a?.sequence - b?.sequence,
  );
  const registerDetailEntries = data?.registerLog?.registerDetailEntries || [];
  // Get all field IDs that have comments
  const commentedFieldIds = map(
    commentData?.comments?.filter((c) => c?.fieldType === 'detail'),
    'fieldId',
  );

  useEffect(() => {
    eva.replace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerDetails]);

  const getDetailValue = (detailId) => {
    const entry = registerDetailEntries?.find((e) => e?.id === detailId);
    const value = entry?.registerLogDetailEntries?.value;
    if (moment(value, TIME_24_HOUR_FORMAT, true)?.isValid()) {
      return moment(value, TIME_24_HOUR_FORMAT).format(TIME_12_HOUR_FORMAT);
    }
    return value || '-';
  };
  const getDetailId = (detailId) => {
    const entry = registerDetailEntries?.find((e) => e?.id === detailId);
    return entry?.registerLogDetailEntries?.id;
  };

  // Check if field has a comment
  const hasComment = (fieldId) => includes(commentedFieldIds, fieldId);

  // Render comment button using RegisterCommentPopUp
  const renderCommentButton = (record) => {
    const hasFieldComment = hasComment(record?.id);
    const previousComments = filter(
      data?.registerLog?.registerDetailEntries,
      (item) => item?.registerLogDetailEntries?.id === record?.id,
    );

    return (
      <RegisterCommentPopUp
        previousHistoryData={previousComments}
        fieldData={record}
        data={data}
        onClick={() => {
          setSelectedField(record);
          setShowCommentModal(true);
        }}
        hasComment={hasFieldComment}
        fieldType="detail"
      />
    );
  };

  // Table columns configuration
  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Field Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (text) => <EllipsisText text={text || '-'} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'end',
      render: (text, record) => <div>{renderCommentButton(record)}</div>,
    },
  ];

  // Prepare data source for table
  const dataSource = map(registerDetails, (detail) => ({
    key: detail?.id,
    id: getDetailId(detail?.id),
    name: detail?.name,
    value: getDetailValue(detail?.id),
  }));

  return (
    <>
      {showCommentModal && selectedField && (
        <RegisterCommentModal
          showModal={showCommentModal}
          setShowModal={setShowCommentModal}
          data={data}
          fieldData={selectedField}
          setCommentData={setCommentData}
          commentData={commentData}
          fieldType="detail"
          refetch={refetch}
        />
      )}

      <div className="approver-stage-details">
        {registerDetails?.length > 0 && (
          <div className="approver-stage-details-header d-flex">
            <i data-eva="checkmark-square-outline" className="mr-16" />
            <h2>Register Details</h2>
          </div>
        )}
        {registerDetails?.length > 0 ? (
          <>
            {isDesktopViewport ? (
              <Card
                loading={loading}
                className="mb-24 register-scrollable-table-card"
              >
                <CommonTable
                  columns={columns}
                  data={dataSource}
                  rowKey={(obj) => obj?.id}
                  scroll={{ y: 500 }}
                  onScroll={onScroll}
                />
              </Card>
            ) : (
              <div
                className="register-mobile-scroll-wrapper mb-24"
                onScroll={onScroll}
              >
                {loading && dataSource?.length === 0 ? (
                  <Card loading={loading} />
                ) : (
                  map(dataSource, (detail, index) => (
                    <Card
                      key={detail?.key || index}
                      className="mb-16 register-entry-mobile-card"
                    >
                      <div className="mb-16">
                        <h3 className="mobile-card-title">
                          {index + 1}. {detail?.name}
                        </h3>
                        <div className="entry-field-item">
                          <div className="entry-field-label">Value:</div>
                          <div className="entry-field-value">
                            {detail?.value}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex justify-center">
                        {renderCommentButton(detail)}
                      </div>
                    </Card>
                  ))
                )}
              </div>
            )}
          </>
        ) : (
          <Card loading={loading} className="mb-24">
            <div className="text-center text-secondary">No details found</div>
          </Card>
        )}
      </div>
    </>
  );
};

export default RegisterLogDetailsSection;
